// src/features/dashboard/components/StatCard.tsx
import {
  Users,
  Server,
  TrendingUp,
  Wallet,
  ShoppingCart,
  LifeBuoy,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';
import { GlassCard } from '../../../app/components/ui/GlassCard';
import { useCountUp } from '../hooks/useCountUp';
import { formatStatValue } from '../utils/formatters';
import type { StatCardData, StatIconKey } from '../types/dashboard.types';

const ICON_MAP: Record<StatIconKey, typeof Users> = {
  customers: Users,
  services: Server,
  sales: TrendingUp,
  wallet: Wallet,
  orders: ShoppingCart,
  tickets: LifeBuoy,
};

const TREND_ICON = { up: ArrowUp, down: ArrowDown, flat: Minus } as const;

interface StatCardProps {
  data: StatCardData;
  /** stagger index used to offset the entrance animation */
  index?: number;
}

export function StatCard({ data, index = 0 }: StatCardProps) {
  const Icon = ICON_MAP[data.icon];
  const TrendIcon = TREND_ICON[data.trend.direction];
  const animatedValue = useCountUp(data.value);

  const trendToneClass =
    data.trend.direction === 'up'
      ? 'vania-trend--up'
      : data.trend.direction === 'down'
        ? 'vania-trend--down'
        : 'vania-trend--flat';

  return (
    <GlassCard
      interactive
      className="vania-stat-card"
      style={{ animationDelay: `${index * 60}ms` }}
      data-animate="fade-in"
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        <span className={`vania-icon-badge vania-icon-badge--${data.accent}`}>
          <Icon size={19} strokeWidth={2.2} />
        </span>
        <span className={`vania-trend shrink-0 ${trendToneClass}`}>
          <TrendIcon size={12} strokeWidth={2.5} />
          {data.trend.percent > 0 ? `${data.trend.percent}٪` : '—'}
        </span>
      </div>

      <div className="mt-5 flex flex-col gap-1.5">
        <span className="vania-stat-value text-2xl sm:text-[1.75rem] font-bold tabular-nums leading-none text-[var(--vania-text-primary,#f5f6fa)]">
          {formatStatValue(animatedValue, data.format)}
        </span>
        <span className="vania-stat-label text-sm font-medium text-[var(--vania-text-secondary,#a3aab8)]">
          {data.label}
        </span>
      </div>

      <div className="mt-4 pt-3 border-t border-[var(--vania-border-glass,rgba(255,255,255,0.08))]">
        <span className="text-xs text-[var(--vania-text-tertiary,#6b7280)]">{data.subtitle}</span>
      </div>
    </GlassCard>
  );
}
